import { useCreateContact } from "@jugl-web/domain-resources/contacts/hooks/useCreateContact";
import { ClientContact } from "@jugl-web/rest-api";
import {
  Button,
  Dialog,
  MobileDrawer,
  CXDrawerHeader,
  DrawerHeader,
} from "@jugl-web/ui-kit";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  ContactDetailsFormFields,
  ContactDetailsFormValues,
  EMPTY_CONTACT_DETAILS_FORM_VALUES,
} from "../ContactDetailsFormFields";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { ReactComponent as PeopleIcon } from "./assets/people.svg";

export interface NewContactDialogProps {
  entityId: string;
  isOpen: boolean;
  onRequestClose: () => void;
  onContactCreated?: (contact: ClientContact) => void;
}

export const NewContactDialog: FC<NewContactDialogProps> = ({
  entityId,
  isOpen,
  onRequestClose,
  onContactCreated,
}) => {
  const { isMobile } = useAppVariant();

  const { createContact, isLoading: isCreatingContact } = useCreateContact({
    entityId,
  });

  const { t } = useTranslations();

  const form = useForm<ContactDetailsFormValues>({
    defaultValues: EMPTY_CONTACT_DETAILS_FORM_VALUES,
  });

  const { reset, handleSubmit } = form;

  const onSubmit = handleSubmit(async (values) => {
    const contact = await createContact({
      name: values.name,
      email: values.email,
      phone: values.phone,
    });

    if (contact) {
      onContactCreated?.(contact);
      onRequestClose();
    }
  });

  useEffect(() => {
    if (isOpen) {
      reset(EMPTY_CONTACT_DETAILS_FORM_VALUES);
    }
  }, [isOpen, reset]);

  const title = t({
    id: "client-support-page.new-contact",
    defaultMessage: "New Customer",
  });

  const detailsTitle = t({
    id: "client-support-page.enter-contact-details",
    defaultMessage: "Enter Customer Details",
  });

  const detailsDescription = t({
    id: "client-support-page.email-or-phone-required",
    defaultMessage:
      "An email or Phone number is required to create new customer",
  });

  const cancelLabel = t({
    id: "common.cancel",
    defaultMessage: "Cancel",
  });

  const detailsSection = (
    <div className="flex flex-col gap-1">
      <span className="font-secondary font-medium text-dark">
        {detailsTitle}
      </span>
      <span className="font-secondary text-[13px] text-dark-600">
        {detailsDescription}
      </span>
    </div>
  );

  if (isMobile) {
    return (
      <MobileDrawer isOpen={isOpen} onClose={onRequestClose}>
        <FormProvider {...form}>
          <MobileDrawer.Content className="pb-0">
            <CXDrawerHeader
              title={title}
              onClose={onRequestClose}
              onSubmit={onSubmit}
              submitLabel={t({ id: "common.save", defaultMessage: "Save" })}
              isSubmitDisabled={isCreatingContact}
              className="px-0"
            />
            <div className="flex flex-col gap-8 pt-4">
              <div className="flex items-stretch gap-4">
                <div className="w-px shrink-0 self-stretch rounded-[1px] bg-[#E0E0E0]" />
                {detailsSection}
              </div>
              <ContactDetailsFormFields />
            </div>
          </MobileDrawer.Content>
        </FormProvider>
      </MobileDrawer>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onRequestClose}
      className="flex max-h-[90vh] w-[780px] flex-col rounded-2xl"
    >
      <DrawerHeader
        type="title"
        title={
          <div className="flex items-center gap-3">
            <PeopleIcon className="shrink-0" />
            {title}
          </div>
        }
        onClose={onRequestClose}
      />
      <FormProvider {...form}>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="flex flex-col gap-8 px-14 pt-10 pb-12">
            {detailsSection}
            <ContactDetailsFormFields />
          </div>
          <div className="flex h-[72px] shrink-0 items-center justify-end gap-4 border-0 border-t border-solid border-dark-100 px-6">
            <Button
              variant="tertiary"
              className="w-[120px]"
              onClick={onRequestClose}
            >
              {cancelLabel}
            </Button>
            <Button
              className="w-[300px]"
              iconEnd={<ArrowRightIcon />}
              onClick={onSubmit}
              isDisabled={isCreatingContact}
            >
              {t({
                id: "client-support-page.add-contact",
                defaultMessage: "Add Customer",
              })}
            </Button>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
};
